import ENDPOINTS from '../../../constants/apiEndPoints';
import { axiosInstance } from '../../axiosInstance';

export enum FormStatuses {
  DRAFT = 'draft',
  PUBLISHED = 'published',
  ARCHIVED = 'archived',
}

export type FormUserRole = {
  _id: string;
  roleType?: {
    _id: string;
    name: {
      en: string;
      he: string;
    };
    icon: string;
  };
  icon: string;
  color: string;
};

export type FormCreatedBy = {
  _id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  roles?: FormUserRole[];
};
export type FormDepartment = {
  _id: string;
  name: string;
  color?: string;
  icon?: string;
};

export enum QuestionType {
  SHORT_TEXT = 'short-answer',
  MULTIPLE_CHOICE = 'multiple-choice',
  CHECKBOX = 'checkbox',
  LINEAR_SCALE = 'linear-scale',
  RATING = 'rating',
  DATE = 'date',
  TIME = 'time',
  RANGE = 'range',
  FILE_UPLOAD = 'file-upload',
  DROPDOWN = 'dropdown',
}

export enum RangeType {
  NUMERIC = 'numeric',
  TEXTUAL = 'textual',
  TEMPERATURE = 'temperature',
}
export enum RangeFormType {
  FROM_TO = 'from-to',
  BETWEEN = 'between',
  RANGE = 'range',
}

export enum AllowedFileTypes {
  PHOTO = 'photo',
  VIDEO = 'video',
  AUDIO = 'audio',
  PDF = 'pdf',
  WORD = 'word',
  PPT = 'ppt',
}

export type FormQuestionOption = {
  label: string;
  image?: string;
  isOther?: boolean;
  key: string;
  _id?: string;
};

export type FormQuestionLinearScaleType = {
  min: number;
  max: number;
  minLabel: string;
  maxLabel: string;
};

export type FormQuestionRatingType = {
  max: number;
  icon: string;
};

export type FormQuestion = {
  _id: string;
  title: string;
  displayOrder: number;
  type: QuestionType;
  required: boolean;
  image?: string;
  linearScale?: FormQuestionLinearScaleType;
  timeConfig?: {
    range: boolean;
  };
  rating?: FormQuestionRatingType;
  dateConfig: {
    range: boolean;
  };
  rangeConfig?: {
    rangeType: RangeType;
    form: RangeFormType;
    min?: number;
    max?: number;
    minLabel?: string;
    maxLabel?: string;
    unit?: string;
    canUploadImage: boolean;
  };
  fileUploadConfig: {
    allowedTypes: AllowedFileTypes[];
    maxFileSizeMB: number;
    maxFiles: number;
  };
  options: FormQuestionOption[];
  __v: number;
  createdAt: string;
  updatedAt: string;
};
export type FormBranches = {
  _id: string;
  name: string;
  status: string;
  type: string;
};

export type FormSubBranches = {
  _id: string;
  name: string;
  status?: string;
  type?: string;
};
export type FormClients = {
  _id: string;
  name: string;
  slug: string;
  status: string;
  type: string;
  logo: {
    url: string;
    type: string;
  };
};
// Types for form data based on actual API response
export type FormsData = {
  _id: string;
  title: string;
  description: string;
  status: FormStatuses;
  allQuestionsRequired: boolean;
  countRespondents: boolean;
  isAnonymous: boolean;
  formForCustomers: boolean;
  createdBy: FormCreatedBy;
  recipients: {
    users: FormCreatedBy[];
    departments: FormDepartment[];
  };
  confirmationMessage?: string;
  isSingleResponse: boolean;
  questions: FormQuestion[];
  createdAt: string;
  updatedAt: string;
  __v: number;
  clients: FormClients[];
  branches: FormBranches[];
  subBranches?: FormSubBranches[];
  responsesCount: number;
};
export type GetAllFormParams = {
  isPaginated?: boolean;
  limit?: number;
  page?: number;
  search?: string;
  status?: FormStatuses;
  sortBy?: string;
  sortOrder?: string;
  clientId?: string | string[];
  branchId?: string | string[];
  department?: string | string[];
  formForCustomers?: boolean;
  isAnonymous?: boolean;
};

export interface FormsResponse {
  data: FormsData[];
  pagination: {
    total: number;
    totalPages: number;
    page: number;
    limit: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

export interface createFormParams {
  title: string;
  description?: string;
  status: FormStatuses;
  clientId?: string[];
  branchId?: string[];
  allQuestionsRequired: boolean;
  countRespondents: boolean;
  isAnonymous: boolean;
  formForCustomers: boolean;
  isSingleResponse: boolean;
  recipients: {
    users?: string[];
    departments?: string[] | 'all'; // Can be array of strings or string "all"
  };
  confirmationMessage?: string;
  questions?: FormQuestion[];
  scheduling?: any; // Optional - Scheduling configuration
}

export interface FormResponsePayload {
  responder?: {
    userId?: string;
    email?: string;
    name?: string;
  };
  answers: {
    questionId: string;
    value: any;
  }[];
}

export const handleGetAllFormsService = async (
  params?: GetAllFormParams,
): Promise<any> => {
  try {
    const response = await axiosInstance.get(ENDPOINTS.GET_CREATE_FORM, {
      params,
    });
    if (!response?.data?.data) {
      return [];
    }
    if (params?.isPaginated) {
      return response?.data;
    }
    return response?.data?.data;
  } catch (error) {
    console.error('Forms Service Debug - Error:', error);
    return [];
  }
};

export const handleGetFormsByIdsService = async (
  payload: Record<string, any>,
): Promise<any> => {
  try {
    const response = await axiosInstance.post(
      ENDPOINTS.GET_FORMS_BY_IDS,
      payload,
    );
    return response?.data;
  } catch (error) {
    console.error('Forms By IDs Service Debug - Error:', error);
    return [];
  }
};

export const handleBulkChangeFormStatusService = async (payload: {
  formIds: string[];
  newStatus: FormStatuses;
}) => {
  const response = await axiosInstance.post(
    ENDPOINTS.BULK_CHANGE_FORM_STATUS,
    payload,
  );
  return response?.data;
};

export const handleCreateFormService = async (payload: createFormParams) => {
  const response = await axiosInstance.post(ENDPOINTS.GET_CREATE_FORM, payload);
  return response?.data;
};

export const handleUpdateFormService = async (
  formId: string,
  payload: Partial<createFormParams>,
) => {
  const response = await axiosInstance.put(
    ENDPOINTS.UPDATE_FORM(formId),
    payload,
  );
  return response?.data;
};

export const handleGetFormWithAnswersService = async (params: {
  formId: string;
}): Promise<any> => {
  try {
    const response = await axiosInstance.get(
      ENDPOINTS.GET_CREATE_FORM_WITH_ANSWERS(params.formId),
    );
    return response?.data?.data;
  } catch (error) {
    console.error('Forms Service Debug - Error:', error);
    return [];
  }
};

export const handleGetFormByIdService = async (params: {
  id: string;
  userId?: string;
}): Promise<any> => {
  try {
    const response = await axiosInstance.get(
      ENDPOINTS.GET_FORM_BY_ID(params.id),
      { params: { userId: params.userId } },
    );
    return response?.data?.data;
  } catch (error) {
    console.error('Forms Service Debug - Error:', error);
    throw error;
  }
};

export const handleSubmitFormResponseService = async (
  formId: string,
  payload: Partial<FormResponsePayload>,
) => {
  const response = await axiosInstance.post(
    ENDPOINTS.GET_CREATE_FORM_WITH_ANSWERS(formId),
    payload,
  );
  return response?.data;
};

export type ShareFormPayload = {
  isAnonymous: boolean;
  clientIds: string[];
  branchIds?: string[];
  subBranchIds?: string[];
  userIds: string[];
  url: string;
  shareLinkVia: string | undefined;
};

export const handleShareFormService = async (
  formId: string,
  payload: ShareFormPayload,
) => {
  const response = await axiosInstance.post(
    ENDPOINTS.SHARE_FORM(formId),
    payload,
  );
  return response?.data;
};
